<template>
  <div class="medium-12 columns">
    <div class="templates__list-search">
      <fluent-icon icon="search" class="search-icon" size="16" />
      <input
        ref="search"
        v-model="query"
        type="search"
        :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
        class="templates__search-input"
      />
    </div>
    <div class="template__list-container">
      <div v-if="uiFlags.isFetchingByInbox" class="template__list-loading">
        <spinner color-scheme="dark" />
      </div>
      <template v-else>
        <div
          v-for="(template, i) in filteredTemplateMessages"
          :key="template.id"
        >
          <button
            class="template__list-item"
            @click="$emit('onSelect', template)"
          >
            <div>
              <div class="flex-between">
                <p class="label-title">
                  {{ template.name }}
                </p>
                <span class="label-lang label">
                  {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.CATEGORY') }} :
                  {{ $t(`WHATSAPP_TEMPLATES.CATEGORY.${template.category}`) }}
                </span>
              </div>
              <div class="content-template" v-if="getTemplateHeader(template)">
                <p class="strong">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER') }}
                </p>
                <p v-if="getTemplateHeader(template).text" class="label-body">
                  {{ getTemplateHeader(template).text }}
                </p>
                <fluent-icon
                  v-else
                  :icon="getIconTemplateHeader(template)"
                  size="20"
                />
              </div>
              <div class="content-template">
                <p class="strong">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.BODY') }}
                </p>
                <p class="label-body">{{ bodyPreviewText(template) }}</p>
              </div>
              <div
                v-if="getTemplateButtons(template).length"
                class="content-template"
              >
                <p class="strong">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.BUTTONS') }}
                </p>
                <div class="template-buttons">
                  <div
                    v-for="(button, index) in getTemplateButtons(template)"
                    :key="index"
                    class="template-button"
                  >
                    {{ buttonLabel(button) }}
                  </div>
                </div>
              </div>
              <div v-if="getTemplateFooter(template)">
                <p class="strong">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.FOOTER') }}
                </p>
                <p class="label-body">{{ getTemplateFooter(template) }}</p>
              </div>
            </div>
          </button>
          <hr
            v-if="i != filteredTemplateMessages.length - 1"
            :key="`hr-${i}`"
          />
        </div>
        <div v-if="!filteredTemplateMessages.length">
          <p>
            <span v-html="noTemplatesFoundMessage"></span>
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import Spinner from 'shared/components/Spinner';
import adminMixin from 'dashboard/mixins/isAdmin';

export default {
  mixins: [adminMixin],
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
    withAttachments: {
      type: Boolean,
      default: true,
    },
    removeExistingParameterizations: {
      type: Boolean,
      default: false,
    },
    templateFilter: {
      type: Object,
      default: () => {},
    },
    templatesAdmin: {
      type: Array,
      default: () => [],
    },
  },
  components: { FluentIcon, Spinner },
  data() {
    return {
      query: '',
      parameterizedTemplateTitles: [],
    };
  },
  watch: {
    inboxId: {
      immediate: true,
      handler() {
        this.loadExistingParameterizations();
      },
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'templateParameterizations/getUIFlags',
    }),
    whatsAppTemplateMessages() {
      let data = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      ).filter(template => template.status.toLowerCase() === 'approved');

      if (this.templateFilter?.marketing === false) {
        data = data.filter(template => template.category !== 'MARKETING');
      }

      if (this.templateFilter?.authentication === false) {
        data = data.filter(template => template.category !== 'AUTHENTICATION');
      }

      if (this.isAdmin == false) {
        const templatesAdmin = this.templatesAdmin || [];
        // Remove templates para usuários não administradores
        data = data.filter(template => !templatesAdmin.includes(template.name));
      }

      return data;
    },
    filteredTemplateMessages() {
      let whatsAppTemplates = this.whatsAppTemplateMessages;

      if (!this.withAttachments) {
        return whatsAppTemplates.filter(
          template =>
            this.matchTemplateNameQuery(template.name) &&
            !template.components.some(
              ({ format }) =>
                format === 'DOCUMENT' ||
                format === 'IMAGE' ||
                format === 'VIDEO'
            )
        );
      }

      if (this.removeExistingParameterizations) {
        whatsAppTemplates = whatsAppTemplates
          .filter(
            template =>
              !this.parameterizedTemplateTitles.includes(template.name)
          )
          .filter(template => {
            return template.components.some(el => el?.text?.includes('{{1}}'));
          });
      }

      return whatsAppTemplates.filter(template =>
        this.matchTemplateNameQuery(template.name)
      );
    },
    noTemplatesFoundMessage() {
      if (!this.query) {
        if (!this.whatsAppTemplateMessages.length) {
          return this.$t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE');
        }
        return this.$t('WHATSAPP_TEMPLATES.PICKER.ALL_TEMPLATES_PARAMETERIZED');
      }
      if (
        this.whatsAppTemplateMessages.some(template =>
          this.matchTemplateNameQuery(template.name)
        )
      ) {
        return this.$t(
          'WHATSAPP_TEMPLATES.PICKER.TEMPLATE_ALREADY_PARAMETERIZED',
          {
            templateName: this.formatTemplateName(this.query),
          }
        );
      } else {
        return this.$t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND', {
          templateName: this.query,
        });
      }
    },
  },
  methods: {
    ...mapActions('templateParameterizations', ['getAllByInbox']),
    async loadExistingParameterizations() {
      if (!this.removeExistingParameterizations || !this.inboxId) {
        this.parameterizedTemplateTitles = [];
        return;
      }

      const whatsAppTemplateMessages = this.whatsAppTemplateMessages;
      const shouldGetFromStore =
        whatsAppTemplateMessages.length <= 15 ||
        whatsAppTemplateMessages.length >= 100;

      const parameterizations = shouldGetFromStore
        ? this.$store.getters['templateParameterizations/getParameterizations']
        : await this.getAllByInbox({ inboxId: this.inboxId });

      this.parameterizedTemplateTitles = parameterizations
        .map(({ template }) => template?.title)
        .filter(Boolean);
    },
    getIconTemplateHeader(template) {
      const { format } = this.getTemplateHeader(template);

      switch (format) {
        case 'IMAGE':
          return 'image';
        case 'DOCUMENT':
          return 'document';
        case 'VIDEO':
          return 'video-add';
      }
    },
    getTemplateHeader(template) {
      return template.components.find(({ type }) => type === 'HEADER');
    },
    isAuthenticationTemplate(template) {
      return template.category?.toUpperCase() === 'AUTHENTICATION';
    },
    getTemplateBody(template) {
      return template.components.find(({ type }) => type === 'BODY')?.text;
    },
    bodyPreviewText(template) {
      if (this.isAuthenticationTemplate(template)) {
        return this.$t(
          'WHATSAPP_TEMPLATES.PARSER.AUTHENTICATION_BODY_PREVIEW',
          { code: '{{1}}' }
        );
      }

      return this.getTemplateBody(template);
    },
    buttonLabel(button) {
      if (button.text) return button.text;
      if (button.type === 'OTP') {
        return this.$t('WHATSAPP_TEMPLATES.PARSER.AUTHENTICATION_BUTTON_LABEL');
      }

      return '';
    },
    getTemplateFooter(template) {
      return template.components.find(({ type }) => type === 'FOOTER')?.text;
    },
    getTemplateButtons(template) {
      const buttonsComponent = template.components.find(
        ({ type }) => type === 'BUTTONS'
      );
      return buttonsComponent?.buttons || [];
    },
    matchTemplateNameQuery(templateName) {
      if (this.query.trim() === '') return true;
      const queryformat = this.formatTemplateName(this.query);
      return (
        templateName.toLowerCase().includes(this.query.trim().toLowerCase()) ||
        templateName.toLowerCase().includes(queryformat)
      );
    },
    formatTemplateName(templateName) {
      return templateName
        .replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map(word => word.toLowerCase())
        .join('_');
    },
  },
};
</script>

<style scoped lang="scss">
.flex-between {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-one);
}

.templates__list-search {
  align-items: center;
  background-color: var(--s-25);
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--s-100);
  display: flex;
  margin-bottom: var(--space-one);
  padding: 0 var(--space-one);

  .search-icon {
    color: var(--s-400);
  }

  .templates__search-input {
    background-color: transparent;
    border: var(--space-large);
    font-size: var(--font-size-mini);
    height: unset;
    margin: var(--space-zero);
  }
}
.template__list-container {
  background-color: var(--s-25);
  border-radius: var(--border-radius-medium);
  max-height: 30rem;
  overflow-y: auto;
  padding: var(--space-one);

  .template__list-loading {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 5rem;
  }

  .template__list-item {
    border-radius: var(--border-radius-medium);
    cursor: pointer;
    display: block;
    padding: var(--space-one);
    text-align: left;
    width: 100%;

    &:hover {
      background-color: var(--w-50);
    }

    .content-template {
      margin-bottom: var(--space-one);
    }

    .label-title {
      font-size: var(--font-size-small);
    }

    .label-category {
      margin-top: var(--space-two);
      text-transform: uppercase;

      span {
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-bold);
      }
    }

    .label-body {
      font-family: monospace;
    }
    .template-buttons {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-width: 320px;
      .template-button {
        display: flex;
        padding: 5px 10px;
        align-items: center;
        justify-content: center;
        border: var(--space-micro) solid var(--w-400);
        border-radius: var(--border-radius-normal);
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-bold);
        color: var(--w-400);
        margin-right: var(--space-smaller);
        margin-left: var(--space-smaller);
        margin-bottom: var(--space-smaller);
      }
    }
  }
}

.strong {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
}

hr {
  border-bottom: 1px solid var(--s-100);
  margin: var(--space-one) auto;
  max-width: 95%;
}
</style>